import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Star, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Real Zillow reviews
const realTestimonials = [
  {
    id: "1",
    name: "Jacobo Belmonte",
    location: "Jurupa Valley, CA",
    text: "We've now worked with Raad not just once or twice, but three times — and that alone speaks volumes about the level of service he provides. Every experience has been outstanding. His knowledge of the market, attention to detail, and genuine care for his clients make him stand out.",
    rating: 5,
    date: "Mar 2026",
    type: "Sold a Single Family home",
  },
  {
    id: "2",
    name: "Warren K",
    location: "Corona, CA",
    text: "While interviewing several agents, our neighbor recommended Raad to us. He immediately distinguished himself from the rest by presenting a simple but realistic overview of the selling process. His honesty and professionalism won us over instantly.",
    rating: 5,
    date: "Mar 2026",
    type: "Sold a Single Family home",
  },
  {
    id: "3",
    name: "Nikki Yik",
    location: "Corona, CA",
    text: "Raad did an outstanding job selling my mom's house. Professional, compassionate, and highly skilled — he priced it right, marketed beautifully, negotiated expertly, and made the whole process seamless. We couldn't have asked for a better agent.",
    rating: 5,
    date: "Feb 2026",
    type: "Sold a Single Family home",
  },
  {
    id: "4",
    name: "Monica Rosas",
    location: "Murrieta, CA",
    text: "We had a fantastic experience with Raad! He sold our home quickly, guided us smoothly through closing of escrow, and used strong negotiation skills to get us into our new home. He made what could have been a stressful process feel easy.",
    rating: 5,
    date: "Feb 2026",
    type: "Bought and sold a Single Family home",
  },
  {
    id: "5",
    name: "Melissa LG",
    location: "Bakersfield, CA",
    text: "Raad was really helpful in our home buying experience. He really looked out for us and made house hunting an easy and very informative experience. He educated us on what to look for and what questions to ask. Truly a great agent.",
    rating: 5,
    date: "Feb 2026",
    type: "Bought a Single Family home",
  },
  {
    id: "6",
    name: "Bejenan",
    location: "Fontana, CA",
    text: "Raad is an amazing agent to work with. He negotiated our sale with such professionalism. Raad did not make us feel like this was just a transaction but was always attentive to our needs and concerns throughout the entire process.",
    rating: 5,
    date: "Oct 2025",
    type: "Sold a Single Family home",
  },
  {
    id: "7",
    name: "Raymundo Antuna",
    location: "Crestline, CA",
    text: "If I could give Raad and his team 10 stars I would. I was already extremely surprised that he was able to help me find a beautiful home in my price range despite everything going on in the market. His dedication is unmatched.",
    rating: 5,
    date: "Sep 2025",
    type: "Bought a Single Family home",
  },
  {
    id: "8",
    name: "Kathy Cota",
    location: "Hesperia, CA",
    text: "This is the second home Raad has helped us purchase. He also helped us sell our first home, which I initially bought with his assistance. Throughout the entire process, Raad was professional, patient, and always had our best interests at heart.",
    rating: 5,
    date: "Sep 2025",
    type: "Bought a Single Family home",
  },
  {
    id: "9",
    name: "Lisa Reyes",
    location: "Glendora, CA",
    text: "Raad bent over backwards to get me my home! I've spoken to sooooo many realtors and they all say the same thing 'I can help you with bad credit' but Raad made it happen. He is the real deal and I am forever grateful.",
    rating: 5,
    date: "Jan 2025",
    type: "Bought a Condo home",
  },
  {
    id: "10",
    name: "Fidel",
    location: "Redlands, CA",
    text: "Raad did an outstanding job assisting us with the purchase of our first home. His expertise in the market was really mesmerizing to watch first hand. Raad was very involved and made the entire process stress-free.",
    rating: 5,
    date: "Feb 2023",
    type: "Bought a Single Family home",
  },
];

export default function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState(realTestimonials);

  const fetchTestimonials = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      if (response.data.length > 0) {
        setTestimonials(response.data);
      }
    } catch (_err) {
      // Fallback to default Zillow testimonials - expected when no DB data exists
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  return (
    <div className="page-transition" data-testid="testimonials-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl space-y-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-px w-12 bg-[#D4AF37]" />
                <span className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm">
                  Verified Reviews
                </span>
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-white">
                Client <span className="text-[#D4AF37]">Reviews</span>
              </h1>
            </div>
            <p className="text-xl text-zinc-300 leading-relaxed">
              Don't take my word for it. These are real reviews from real clients 
              on Zillow. All 5 stars.
            </p>
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={`hero-star-${n}`} className="w-6 h-6 fill-[#D4AF37] text-[#D4AF37]" />
                  ))}
                </div>
                <span className="text-white font-bold text-2xl">5.0</span>
              </div>
              <span className="text-zinc-500">|</span>
              <span className="text-zinc-300 font-semibold">5-Star Reviews on Zillow</span>
              <span className="text-zinc-500">|</span>
              <span className="text-zinc-300 font-semibold">150+ Homes Sold</span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="card-dark p-8 space-y-5 animate-fadeInUp hover:border-[#D4AF37]/50 transition-colors relative"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`testimonial-item-${index}`}
              >
                <div className="absolute top-6 right-6 text-[#D4AF37] text-6xl font-serif opacity-15">"</div>
                
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={`rating-${testimonial.id}-${n}`} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                  ))}
                </div>

                {testimonial.type && (
                  <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-wider">
                    {testimonial.type}
                  </p>
                )}
                
                <p className="text-zinc-300 leading-relaxed relative z-10">
                  {testimonial.text}
                </p>
                
                <div className="pt-4 border-t border-white/10 flex justify-between items-end">
                  <div>
                    <p className="text-white font-semibold">{testimonial.name}</p>
                    <p className="text-zinc-400 text-sm">{testimonial.location}</p>
                  </div>
                  {testimonial.date && (
                    <p className="text-zinc-500 text-xs">{testimonial.date}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-black border-t border-[#D4AF37]/20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center space-y-8">
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">
            Ready to Be My{" "}
            <span className="text-[#D4AF37]">Next Success Story?</span>
          </h2>
          <p className="text-zinc-400 text-lg">
            Join 150+ happy homeowners who trusted me with their biggest investment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+19092335411">
              <Button
                className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] rounded-none font-bold uppercase tracking-widest px-8 py-6"
                data-testid="testimonials-call-button"
              >
                Call 909.233.5411
              </Button>
            </a>
            <Link to="/contact">
              <Button
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-black rounded-none font-bold uppercase tracking-widest px-8 py-6"
                data-testid="testimonials-contact-button"
              >
                Send Message
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
